import h from 'hyperscript';

import iconBulb from '@disy/cadenza-icons/bulb.svg';
import iconHelp from '@disy/cadenza-icons/help.svg';
import iconLogout from '@disy/cadenza-icons/logout.svg';
import iconProfile from '@disy/cadenza-icons/profile.svg';
import iconRecord from '@disy/cadenza-icons/record.svg';
import iconRefresh from '@disy/cadenza-icons/refresh.svg';
import iconShortcuts from '@disy/cadenza-icons/shortcuts.svg';
import iconStopwatch from '@disy/cadenza-icons/stopwatch.svg';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { getDisyNamespace } from '@disy/lib/disy';
import { supportsKeyboard } from '@disy/lib/media';
import { openUrl } from '@disy/lib/open-url';
import { icon } from '@disy/ui/icon';
import type { MenuContentElement, MenuItem } from '@disy/ui/menu';
import { setupMenuButton } from '@disy/ui/menu';

import 'ui/data/data';
import type { Data } from 'ui/data/data';
import { Modal } from 'ui/dialog/modal/modal';

import { isFeatureAvailable } from 'cadenza/features';
import { showKeymap } from 'cadenza/hotkeys/showKeymap';
import { theme } from 'cadenza/theming-api';

import i18n from './messages.properties';
import './topnav.css';

class Topnav extends HTMLElement {
  constructor() {
    super();
    setupMenuButton(this, (target) => createMenuContent(target!), { delegate: '.d-topnav-dropdown' });
  }

  connectedCallback() {
    this.classList.toggle('dark-bg', theme('main-header-invert'));
  }
}

function createMenuContent(menuButton: Element) {
  const entries = menuButton.querySelector<Data<MenuItemConfig[]>>('d-data')!.value;
  const additionalContent = menuButton.querySelector('template');
  const content: MenuContentElement[] = entries
    .filter(({ id }) => id !== 'keymap' || supportsKeyboard)
    .map(createEntry);
  if (menuButton.getAttribute('roundtrip-measurement-enabled') === 'true') {
    content.push({
      icon: icon(iconStopwatch),
      label: i18n('roundtripMeasure.label'),
      execute: showRoundTripMeasurement,
    });
  }
  if (menuButton.matches('.d-help-menu')) {
    if (isFeatureAvailable('WORKBOOK_PERFORMANCE_ANALYSIS')) {
      content.push({
        icon: icon(iconRecord),
        label: i18n('queryLogger'),
        execute: () => openUrl(cadenzaUrl('/query-logger')),
      });
    }
  }
  if (additionalContent) {
    content.push(additionalContent.content.cloneNode(true));
  }
  return content;
}

interface MenuItemConfig {
  id: string;
  printName: string;
  url?: string;
  target?: string;
}

function createEntry({ id, printName, url, target }: MenuItemConfig): MenuItem {
  if (id === 'keymap') {
    return {
      icon: icon(iconShortcuts),
      label: printName,
      execute: showKeymap,
    };
  }
  return {
    icon: getIcon(id, url!),
    label: printName,
    url: cadenzaUrl(url!),
    target,
  };
}

function getIcon(id: string, url: string) {
  switch (id) {
    case 'help':
      return icon(url.startsWith('/help-learning/') ? iconBulb : iconHelp);
    case 'logout':
      return icon(iconLogout);
    case 'userprofile':
      return icon(iconProfile);
    case 'reloadRepository':
      return icon(iconRefresh);
  }
}

function showRoundTripMeasurement() {
  const dialog = new Modal({
    title: i18n('roundtripMeasure.label'),
    size: 's',
    content: async () => {
      try {
        const roundTripTime = await startMeasurement();
        return h('p', i18n('roundtripMeasure.result', { roundTripTime }));
      } catch {
        return h('d-alert', {
          type: 'error',
          message: i18n('roundtripMeasure.error'),
        });
      }
    },
  });
  dialog.connectAndShow();
}

function startMeasurement(): Promise<number> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve(Date.now() - start);
      } else {
        reject();
      }
    });
    xhr.addEventListener('error', () => reject());
    xhr.open('GET', getDisyNamespace().contextPath + '/status');
    const start = Date.now();
    xhr.send();
  });
}

customElements.define('d-topnav', Topnav);
